import './SelfPromotion.scss';

import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';

import { canPerformAction } from '../../../utilities/permissionUtils';
import { RANKS } from '../constants';

const indexOrLast = (list, value) => {
  const index = list.indexOf(value);
  return index === -1 ? list.length : index;
};

function SelfPromotion({
  configs = [],
  cycles = [],
  loading,
  getAllConfigs,
  getAllCycles,
  setConfig,
  permissions,
}) {
  const [cycleId, setCycleId] = useState('');
  const [rank, setRank] = useState('');
  const [search, setSearch] = useState('');

  const canManage = canPerformAction(
    'Settings.SelfPromotion.Manage',
    permissions,
  );

  useEffect(() => {
    getAllConfigs();
    getAllCycles();
  }, []);

  const handleAdd = () => {
    if (!cycleId || !rank) return;
    setConfig(parseInt(cycleId), rank, true);
    setCycleId('');
    setRank('');
  };

  const handleToggle = (config) => {
    setConfig(config.cycleId, config.rank, !config.enabled);
  };

  const sortedCycles = [...cycles].sort((a, b) => b.id - a.id);

  // Keep a stable order so rows do not jump around after enabling/disabling:
  // newest cycle first, then by rank.
  const sortedConfigs = useMemo(
    () =>
      [...configs].sort(
        (a, b) =>
          b.cycleId - a.cycleId ||
          indexOrLast(RANKS, a.rank) - indexOrLast(RANKS, b.rank),
      ),
    [configs],
  );

  const filteredConfigs = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sortedConfigs;

    return sortedConfigs.filter((config) =>
      [config.cycleTitle, config.rank, config.enabled ? 'Enabled' : 'Disabled']
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }, [sortedConfigs, search]);

  return (
    <Col className="self-promotion-container">
      <h2>Self Promotion</h2>

      {canManage && (
        <div className="self-promotion-add-form">
          <h5>Add Configuration</h5>
          <div className="form-row">
            <div className="form-group">
              <label>Cycle</label>
              <select
                className="form-control"
                value={cycleId}
                onChange={(e) => setCycleId(e.target.value)}
              >
                <option value="">Select cycle...</option>
                {sortedCycles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Rank</label>
              <select
                className="form-control"
                value={rank}
                onChange={(e) => setRank(e.target.value)}
              >
                <option value="">Select rank...</option>
                {RANKS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            <Button
              variant="primary"
              onClick={handleAdd}
              disabled={!cycleId || !rank || loading}
            >
              Enable
            </Button>
          </div>
        </div>
      )}

      <Row>
        <Col md={12}>
          {configs.length === 0 ? (
            <div className="self-promotion-empty">No configurations yet.</div>
          ) : (
            <div>
              <div className="self-promotion-toolbar">
                <input
                  type="text"
                  className="form-control self-promotion-search"
                  placeholder="Search by cycle, rank or status"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <span className="self-promotion-count">
                  {filteredConfigs.length} of {configs.length} configurations
                </span>
              </div>

              {filteredConfigs.length === 0 ? (
                <div className="self-promotion-empty">
                  No configurations match your search.
                </div>
              ) : (
                <div className="self-promotion-table-container">
                  <table className="table self-promotion-table">
                    <thead>
                      <tr>
                        <th>Cycle</th>
                        <th>Rank</th>
                        <th>Status</th>
                        <th>Last Updated</th>
                        {canManage && <th>Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredConfigs.map((config) => (
                        <tr key={config.id}>
                          <td>{config.cycleTitle}</td>
                          <td>{config.rank}</td>
                          <td>
                            <span
                              className={`status-badge ${config.enabled ? 'enabled' : 'disabled'}`}
                            >
                              {config.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </td>
                          <td>
                            {config.updatedAt
                              ? new Date(config.updatedAt).toLocaleDateString()
                              : '-'}
                          </td>
                          {canManage && (
                            <td>
                              <Button
                                className="btnUnsetActive"
                                variant={config.enabled ? 'danger' : 'success'}
                                onClick={() => handleToggle(config)}
                                disabled={loading}
                              >
                                {config.enabled ? 'Disable' : 'Enable'}
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Col>
      </Row>
    </Col>
  );
}

export default SelfPromotion;
